import { Field, Form, Formik } from "formik";
import { ScrollToFirstError } from "utils";
import { Button, SelectComponent, Textarea, Checkbox } from "shared";
import { Input, DropFileInput } from "shared";
import { InvestmentItem } from "../../../types";
import { OtherAssetValues } from "./types";
import { otherAssetSchema } from "./validationSchemas";
import { RELIANCE_CONSENT_LABEL } from "./constants";

interface OtherAssetFormProps {
  item: InvestmentItem;
  onSubmit: (values: OtherAssetValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedPhoto: (file: { name: string; url: string }) => void;
  onRemovedContract: (file: { name: string; url: string }) => void;
}

const OtherAssetForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedPhoto,
  onRemovedContract,
}: OtherAssetFormProps) => {
  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract" || f.role === "attachment",
  );
  const existingPhotos = (item.files ?? []).filter(
    (f: any) => f.role === "photo",
  );

  const initialValues: OtherAssetValues = {
    name: item.name ?? "",
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    additionalInfo: item.additionalInfo ?? "",
    photoFiles: [],
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={otherAssetSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <ScrollToFirstError />
          <div className="modal-form__fields">
            <Field
              as={Input}
              name="name"
              label="Nazwa"
              type="text"
              error={
                (formik.touched.name || formik.submitCount > 0) &&
                formik.errors.name
                  ? formik.errors.name
                  : undefined
              }
            />

            <Field
              as={Input}
              name="primaryValue"
              label="Wartość"
              type="number"
              step="any"
              precision={2}
              maxLength={12}
              data-suffix={
                currencyOptions.find(
                  (o: any) => o.value === formik.values.currencyUuid,
                )?.label ?? ""
              }
              error={
                (formik.touched.primaryValue || formik.submitCount > 0) &&
                formik.errors.primaryValue
                  ? formik.errors.primaryValue
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions}
              isSearchable={true}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              label="Zdjęcia"
              name="photoFiles"
              id="photoFiles"
              as={DropFileInput}
              accept=".png,.jpg,.jpeg"
              multiple={10}
              maxSize={5}
              description="Maksymalnie 10 plików, maks. 5 MB/plik (JPG, PNG)"
              optional
              showPreview
              existingFiles={
                existingPhotos.length > 0
                  ? existingPhotos
                      .filter((f: any) => f?.file?.name && f?.file?.url)
                      .map((f: any) => ({
                        name: f.file.name,
                        url: f.file.url,
                      }))
                  : undefined
              }
              onServerFileRemoved={onRemovedPhoto}
            />

            <Field
              label="Pliki"
              name="contractFile"
              id="contractFile"
              as={DropFileInput}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              maxSize={5}
              description="Maksymalnie 10 plików, maks. 5 MB/plik (PDF, DOC, DOCX, JPG, PNG)"
              existingFiles={existingContractFiles.map((f: any) => ({
                name: f.file?.name || "Plik",
                url: f.file?.url || "",
              }))}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label={RELIANCE_CONSENT_LABEL}
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default OtherAssetForm;
